import React, { useEffect, useState } from 'react';
import { CookiesProvider } from 'react-cookie';
import { Provider } from 'react-redux';
import { store } from 'redux/store';
import Head from 'next/head';
import localFont from 'next/font/local';
import { useApp } from 'sections/HomePage/hooks/useApp';
import { Environment } from 'sections/HomePage/types';
import { DefaultMetaData } from 'sections/HomePage/data';
import Modal from 'ui/Modal';
import 'styles/styles.scss';

const myFont = localFont({ src: './Montserrat-VariableFont_wght.ttf' });

let environment: Environment = process.env.NODE_ENV;

function MyApp({ Component, pageProps }: any) {
  const {
    checkPageForBlacklist,
    addScripts,
    getLayout,
    otherProps,
    getSchema,
    fetchData,
    isUserLogged
  } = useApp(Component, pageProps);

  const {
    title: propsTitle,
    description: propsDescription,
    keywords,
    path: pathProps = '/',
  } = pageProps;

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (checkPageForBlacklist(pageProps)) return;

    let timeout: any;

    if (!isUserLogged) {
      timeout = setTimeout(() => {
        setIsModalVisible(true);
      }, 10000);
    }

    return () => clearTimeout(timeout);
  }, [isUserLogged]);

  const handleModal = () => (location.href = '/membership');

  return (
    <>
      <Head>
        {!!propsTitle
          ?
          <>
            <title>{propsTitle}</title>
            <meta property="og:title" content={propsTitle} />
            <meta name="twitter:title" content={propsTitle} />
          </>
          :
          <>
            <title>{DefaultMetaData.title}</title>
            <meta property="og:title" content={DefaultMetaData.title} />
            <meta name="twitter:title" content={DefaultMetaData.title} />
          </>
        }
        {!!propsDescription
          ?
          <>
            <meta property="og:description" content={propsDescription} />
            <meta name="twitter:description" content={propsDescription} />
            <meta name="twitter:image" content={propsDescription} />
            <meta name="description" content={propsDescription} />
          </>
          :
          <>
            <meta property="og:description" content={DefaultMetaData.description} />
            <meta name="twitter:description" content={DefaultMetaData.description} />
            <meta name="twitter:image" content={DefaultMetaData.description} />
            <meta name="description" content={DefaultMetaData.description} />
          </>
        }
        {!!keywords
          ?
          <meta name="keywords" content={keywords} />
          :
          <meta
            name="keywords"
            content="digital freight alliance, freight alliance, freight online, online freight marketplace, ocean freight online, logistics member portal"
          />
        }

        <meta property="og:site_name" content="DF Alliance" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.df-alliance.com/images/global/logo.svg" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="DF Alliance" />
        <meta name="twitter:image" content="https://www.df-alliance.com/images/global/logo.svg" />

        <link rel="icon" href="/images/favicon.ico" />

        {environment === 'production'
          ?
          <>
            <meta property="og:url" content={`https://www.df-alliance.com${pathProps}`} />
            <link rel="canonical" href={`https://www.df-alliance.com${pathProps}`} />
            <meta name="google-site-verification" content="mZjCSKb2qrksNA7tbDfY-8aP7DIM2f4H47ukvHAGIZc" />
          </>
          :
          null
        }

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: getSchema() }}
        />
      </Head>

      <main className={myFont.className}>
        {addScripts(environment, pageProps)}
        {getLayout(
          <CookiesProvider>
            <Provider store={store}>
              <Component {...otherProps} />
            </Provider>
          </CookiesProvider>
        )}
      </main>

      <Modal
        title="Welcome to DFA!"
        text="The Digital Freight Alliance is the logistics association created in 2020 by DP World to unite logistics companies in one community, optimizing processes and using digital tools for logistics"
        accentText="All Members receive 5 unique tools"
        btnText="Join now"
        noIcon={true}
        noRedirection={true}
        modalHandler={handleModal}
        closeIcon={true}
        open={isModalVisible}
        setModalVisible={setIsModalVisible}
        contact={''}
      />
    </>
  );
}

export default MyApp;
